import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/context/AuthContext'
import { adminKeys } from '@/lib/adminQueries'
import { keys } from '@/lib/queries'
import { getEcho } from '@/lib/realtime'

/** The lists `App\Events\ReferenceDataChanged` can name. */
export type ReferenceList = 'structure' | 'shifts' | 'templates'

/*
 * Prefixes, not whole keys.
 *
 * `keys.shifts` is keyed on a quarter-hour bucket and `keys.fiveSTemplate` on a
 * work centre, so invalidating one exact key would refresh the bucket or the
 * machine that happens to be on screen and leave every other cached entry
 * holding the old list. TanStack matches a short key against the head of a
 * longer one, so one prefix covers all of them.
 *
 * Sliced off the factories rather than written out as `['shifts']`, so renaming
 * a key in `queries.ts` cannot quietly stop matching here — which would fail
 * silently, because an invalidation that matches nothing is not an error.
 */
const SHIFTS_PREFIX = keys.shifts('').slice(0, 1)
const TEMPLATES_PREFIX = keys.fiveSTemplate('').slice(0, 1)

/**
 * Which cached lists each `.reference.changed` payload makes stale.
 *
 * Two families per list, because the same rows are read through two different
 * endpoints: the ordinary one every role uses (`keys`, in `lib/queries.ts`) and
 * the administrative one behind `/admin` that carries the editing fields
 * (`adminKeys`, in `lib/adminQueries.ts`). The administrator who made the
 * change is looking at the second and everyone else at the first, and the whole
 * point of the event is that both move together — so invalidating only one
 * family would fix this bug for the bystanders and leave a second
 * administrator's screen wrong.
 *
 * `structure` covers plants as well as work centres, because a plant can be
 * renamed or deleted, and every work centre hangs off one — see the channel
 * note in `routes/channels.php`.
 *
 * ## The cost of importing `adminKeys` here
 *
 * It is not free and it was measured. `lib/adminQueries.ts` is otherwise only
 * reached from the lazily-loaded `/administracija` routes, so it builds as its
 * own 5.9 KB chunk that a worker's phone never downloads; naming it from this
 * hook — which mounts on every authenticated screen — folds it into the entry
 * chunk instead, taking that from 685.9 KB to 691.7 KB, 181.1 KB to 182.1 KB
 * gzipped.
 *
 * The alternative is writing `['admin', 'plants']` out by hand here, and that
 * is the thing the note above `keys` in `queries.ts` exists to forbid: a key
 * spelled in two places is a key that eventually matches in only one, and an
 * invalidation that matches nothing is silent. A kilobyte on the wire once per
 * deploy is a worse trade than a list that quietly stops refreshing.
 *
 * Exported for `useRealtime.test.ts`: the mapping is the part worth pinning
 * down, since a key that stops matching produces no error, just a screen that
 * goes back to being stale for as long as its `staleTime`.
 */
export function referenceKeys(list: ReferenceList | string): (readonly unknown[])[] {
  switch (list) {
    case 'structure':
      return [keys.workCenters, adminKeys.workCenters, adminKeys.plants]
    case 'shifts':
      return [SHIFTS_PREFIX, adminKeys.shifts]
    case 'templates':
      return [TEMPLATES_PREFIX, adminKeys.templates]
    default:
      /*
       * A list this build has never heard of — a newer API talking to a tab
       * that has been open across a deploy. Refetching nothing is the right
       * answer: the caches this build holds are the ones it knows the names
       * of, and the short `staleTime` on all of them is the floor anyway.
       */
      return []
  }
}

/**
 * Subscribes to the channels this user is allowed to hear, and turns each
 * signal into a cache invalidation.
 *
 * The subscriptions mirror the roles exactly, because the server will refuse
 * anything else — `routes/channels.php` authorises `company.{id}.managers` only
 * for manager roles at that company, `company.{id}` for anybody at it, and
 * `user.{id}` only for that user. Asking for a channel you cannot join fails
 * silently, so the client does not even try.
 *
 * Nothing here reads event data beyond a `reason` string and, on
 * `.reference.changed`, the name of the list that moved. What actually appears
 * on screen comes from the REST refetch, which is where authorisation lives.
 */
export function useRealtime(): void {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!user) return

    /*
     * Null for the owner, who owns the software rather than holding a role
     * inside a company — see `UserResource`, where this is `company_id`
     * straight off the row. Without a company there is no company channel to
     * join and no tenant whose lists could have changed, so both subscriptions
     * below are skipped rather than built into `company.undefined`.
     */
    const companyId = user.company?.id

    /*
     * The libraries are fetched on demand, so this resolves a tick or two after
     * mount. `cancelled` covers the case where the user logs out — or the
     * effect re-runs — while that download is still in flight: without it we
     * would subscribe to channels nobody is watching any more, and the cleanup
     * would already have run.
     */
    let cancelled = false
    let leave: (() => void) | undefined

    /*
     * `refetchType: 'active'` — refresh what is on screen now, and mark the
     * rest stale so it reloads when next mounted. Without it a busy queue would
     * refetch every cached page of submissions on every event.
     */
    const invalidate = (list: readonly (readonly unknown[])[]) => {
      for (const queryKey of list) {
        queryClient.invalidateQueries({ queryKey, refetchType: 'active' })
      }
    }

    void getEcho().then((echo) => {
      if (!echo || cancelled) return

      /*
       * Join through here, and leave by replaying the list.
       *
       * `echo.leave()` looks a channel up by name and does nothing at all when
       * it misses — no throw, no warning — so a cleanup that spells a name
       * differently from the subscribe leaves a live subscription behind on
       * every sign-out, one per shift on a shared terminal. The two lists used
       * to be written out separately and drifted the moment `managers` became
       * `company.{id}.managers`. Recording the name at the point of joining
       * makes that particular mistake unrepresentable rather than merely
       * caught.
       */
      const joined: string[] = []
      const join = (name: string) => {
        joined.push(name)

        return echo.private(name)
      }

      // Own channel: notifications, tasks, and status changes on submissions
      // this person filed.
      const personal = join(`user.${user.id}`)

      personal.listen('.personal.update', () => {
        invalidate([['notifications'], ['tasks'], ['notifications', 'unread-count']])
      })

      personal.listen('.queue.changed', () => {
        invalidate([['submissions'], ['submission'], ['analytics']])
      })

      if (companyId) {
        /*
         * Everyone at this customer, workers included.
         *
         * This is the channel that fixes "the new machine is not in the list".
         * The lists it announces are a backdrop that every role reads and an
         * administrator occasionally changes, and until this existed the only
         * thing that brought the change to a second browser was that browser's
         * cache expiring — thirty minutes for work centres, and longer on a
         * terminal by the door that nobody ever alt-tabs away from.
         *
         * A worker is authorised here, unlike anywhere else, because the event
         * carries no record: only which list moved. The refetch that follows
         * goes through the ordinary API with the ordinary scoping.
         */
        join(`company.${companyId}`).listen('.reference.changed', (event: { list?: string }) => {
          invalidate(referenceKeys(event?.list ?? ''))
        })
      }

      /*
       * The supervisors' queue. Workers are not authorised for this channel and
       * must not subscribe to it.
       *
       * The name used to be a bare `managers`: one channel shared by every
       * customer, so a submission filed in one factory made every other
       * customer's supervisors refetch. `routes/channels.php` now checks the
       * company in the name, and a client still asking for the old one would
       * simply never be authorised — silently, since a refused subscription
       * only means the app falls back to polling.
       */
      if (user.isManager && companyId) {
        join(`company.${companyId}.managers`).listen('.queue.changed', () => {
          invalidate([['submissions'], ['submission'], ['analytics']])
        })
      }

      leave = () => {
        for (const name of joined) echo.leave(name)
      }
    })

    return () => {
      cancelled = true
      leave?.()
    }
  }, [user, queryClient])
}
